import { App, BQData } from "./app";
import { BQ } from "./bq";
import { SVG } from "../../shared/svg";
import { Api, UpdateOpts } from "./api";
import { Editor, SelectVal } from "./editor";
import { div, input, span, anchor } from "../../shared/dom";
import { Loading } from "../../shared/loading";
import * as CodeMirror from 'codemirror';
import { ApiAttribute, ApiDisplayUpdate, ApiPageUpdate, ApiUpdate } from "./api_data";
import { TablePicker } from "./table_picker";
import { Display, DataConnection, FilterConnection } from "./display";
import { VizSVG } from "./viz_svg";
import { Menu } from "../../shared/menu";
import { Page, VizConnection } from "./page";
import { Ajax } from "../../shared/ajax";
import { DateInputOptions, viewDatePicker, DatePickerType } from "../../shared/date_picker";
import { InputCallbackType } from "../../shared/input_callback";

interface DisplayNode {
  viz: VizConnection,
  group: string;
  position: number,
  element: SVGGElement,
  x: number,
  y: number,
  height: number,
}

interface DisplaySpacer {
  group: string;
  position: number,
  element: SVGRectElement,
}

interface SelectRow {
  container: HTMLElement;
  label_container: HTMLElement;
  val_container: HTMLElement;
  select: HTMLSelectElement;
}

export class EditorPage {

  static render_table(page: Page, viz: Display): HTMLTableElement {
    const table = document.createElement("table");
    const t_head = document.createElement("thead");
    const t_head_row = document.createElement("tr");
    t_head.appendChild(t_head_row);
    table.appendChild(t_head);
    if (viz.query_columns.length > 0) {
      const th = document.createElement("th");
      th.classList.add("idx");
      t_head_row.appendChild(th);
    }

    const render_header = function (col: BQData) {
      const th = document.createElement("th");
      if (page.selected_element === col.column_name) {
        th.classList.add("active");
      }
      let p_label = col.pivot ? "." + col.pivot : "";
      if (col.pivot_target) {
        p_label += " " + col.pivot_target;
      }
      th.innerText = col.column_name + p_label;
      t_head_row.appendChild(th);
    }

    for (const col of viz.query_columns) {
      if (BQ.skip_col(col)) {
        continue;
      }
      render_header(col);
    }

    let selected_key = "";
    const selected_col = Editor.get_selected_column(page);
    if (selected_col) {
      selected_key = Editor.data_key(selected_col.data, true);
    }
    if (viz.query_columns.length > 0) {
      let row_count = 0;
      for (const col of viz.data) {
        row_count = Math.max(row_count, col.rows.length);
      }
      for (let i = 0; i < row_count; i++) {
        const tr = document.createElement("tr");
        const index_td = document.createElement("td");
        index_td.classList.add("idx");
        index_td.innerText = (i + 1).toString();
        tr.appendChild(index_td);
        for (const col of viz.query_columns) {
          const k = Editor.data_key(col, true);
          if (BQ.skip_col(col)) {
            continue;
          }
          const td = document.createElement("td");
          if (selected_key === k) {
            td.classList.add("active");
          }
          for (const data_col of viz.data) {
            if (Editor.data_key(col, true) === Editor.data_key(data_col, true)) {
              if (data_col.rows.length > i) {
                const row = data_col.rows[i];
                td.innerText = row;
              }
            }
          }
          tr.appendChild(td);
        }
        table.appendChild(tr);
      }
    }
    return table;
  }

  static init_data_dom(page: Page, bottom: HTMLElement) {
    const title = div("section-title");
    const selector = div("selector");
    const data = div("option");
    data.innerText = "Data";
    const sql = div("option");
    sql.innerText = "SQL";
    selector.appendChild(data);
    selector.appendChild(sql);
    title.appendChild(selector);

    title.appendChild(div("spacer"));

    const toggle_selector = div("selector");
    const toggle = VizSVG.bottom_panel_icon();
    toggle_selector.appendChild(toggle);
    title.appendChild(toggle_selector);

    const set_selector_state = function () {
      if (page.show_query) {
        data.classList.remove("selected");
        sql.classList.add("selected");
      } else {
        data.classList.add("selected");
        sql.classList.remove("selected");
      }
    }
    if (page.data_panel_open) {
      set_selector_state();
    }

    const set_toggle_state = function (val: boolean) {
      Editor.set_data_panel_open(page, val);
      const editor = document.querySelector("div.editor");
      if (!editor) {
        return;
      }
      if (val) {
        editor.classList.add("data-open");
        set_selector_state();
      } else {
        editor.classList.remove("data-open");
        data.classList.remove("selected");
        sql.classList.remove("selected");
      }
    }

    toggle.onclick = function () {
      const val = !page.data_panel_open;
      set_toggle_state(val);
    }

    data.onclick = function () {
      page.show_query = false;
      set_selector_state();
      if (!page.data_panel_open) {
        set_toggle_state(true);
      }
      App.render();
    }

    sql.onclick = function () {
      page.show_query = true;
      set_selector_state();
      if (!page.data_panel_open) {
        set_toggle_state(true);
      }
      App.render();
    }

    const loading = Loading.overlay()
    loading.style.display = "none";

    bottom.appendChild(title);

    const content = div("content");

    const table_container = div("table-container");
    content.appendChild(table_container);

    const query_container = div("query-container");
    const area = document.createElement("textarea");
    query_container.appendChild(area);
    content.appendChild(query_container);

    content.appendChild(loading);
    bottom.appendChild(content);

    console.log("init editor");
    Editor.editor = CodeMirror.fromTextArea(area, {
      mode: "text/x-sql",
      lineNumbers: true,
      readOnly: true,
    });

  }

  static render_data(page: Page, viz: Display | null, bottom: HTMLElement) {

    if (!bottom.querySelector("div.content")) {
      EditorPage.init_data_dom(page, bottom);
    }

    const table_container = bottom.querySelector("div.table-container") as HTMLElement;
    const query_container = bottom.querySelector("div.query-container") as HTMLElement;
    if (!table_container || !query_container || !Editor.editor || !page) {
      return;
    }

    if (page.show_query) {
      table_container.style.display = "none";
      query_container.style.display = "block";
    } else {
      table_container.style.display = "block";
      query_container.style.display = "none";
    }

    table_container.innerHTML = "";
    Editor.editor.setValue("");
    if (!viz) {
      return;
    }

    const table = EditorPage.render_table(page, viz);
    table_container.appendChild(table);

    Editor.editor.setValue(BQ.build_query(viz));
  }

  static add_icon(el: HTMLElement, col_type: string) {
    switch (col_type) {
      case "INTEGER":
      case "INT64":
      case "FLOAT64":
        el.appendChild(VizSVG.number_icon())
        break;

      case "DATE":
      case "TIMESTAMP":
        el.appendChild(VizSVG.date_icon())
        break;

      case "STRING":
        el.appendChild(VizSVG.text_icon())
        break;

      default:
        break;
    }
  }

  /* TODO: needed?
  static render_picker(container: HTMLElement, selected_set: DataConnection[], filter: ColumnFilter, search: string, cb: Function) {
    container.innerHTML = "";
    if (!VizApp.selected_viz || !VizApp.selected_viz.selected_table) {
      return;
    }
    container.onmousedown = function () {
      container.setAttribute("locked", "true");
      window.setTimeout(function () {
        container.removeAttribute("locked");
      }, 50);
    }
    for (const col of VizApp.selected_viz.selected_table.table.columns) {
      if (filter === ColumnFilter.NumericOnly && !BQ.numeric_col(col.type)) {
        continue;
      }
      if (search !== "") {
        const match = col.name.toLowerCase().indexOf(search.toLowerCase()) === 0;
        if (!match) {
          continue;
        }
      }
      const col_el = div("view-element");
      col_el.innerText = col.name;
      EditorPage.add_icon(col_el, col.type);
      container.appendChild(col_el);

      let selected = false;
      for (const sel of selected_set) {
        if (sel.data.column_name === col.name && sel.data.table_id === col.table_id) {
          selected = true;
        }
      }
      if (selected) {
        col_el.classList.add("disabled");
      } else {
        col_el.classList.add("enabled");
      }

      if (!selected) {
        col_el.onclick = function () {
          cb(col);
        }
      }
    }
  }*/

  private static render_viz_list(page: Page, viz_list: Display[], container: HTMLElement) {

    const render = function (viz: Display) {
      const viz_el = div("view-element", "parent-element");
      viz_el.id = "viz_" + viz.id;
      const s = span();
      s.innerText = viz.title;
      viz_el.appendChild(s);
      viz_el.appendChild(VizSVG.display_icon());
      if (page.selected_element === viz.id) {
        viz_el.classList.add("selected");
      }
      container.appendChild(viz_el);
      const viz_arrow = div("arrow-wrapper");
      viz_arrow.appendChild(VizSVG.arrow());
      viz_el.appendChild(viz_arrow);
      viz_arrow.onmousedown = function (e) {
        e.stopPropagation();
        Editor.set_viz_closed(viz, !viz.closed);
        App.render();
      }

      viz_el.onclick = function (e) {
        e.stopPropagation();
      }

      viz_el.onmousedown = function (e) {
        Editor.set_selected_element(viz.id);
        e.stopPropagation();
        App.render();
      }

      if (viz.closed) {
        viz_el.classList.add("closed");
        return;
      }

      const table_el = div("view-element", "group-element");
      if (viz.selected_table) {
        const l = span("element-label");
        l.innerText = viz.selected_table.table.id;
        table_el.appendChild(l);
      } else {
        table_el.innerText = "Select table...";
      }
      table_el.appendChild(VizSVG.table_icon());
      container.appendChild(table_el);

      const table_arrow = div("arrow-wrapper");
      table_arrow.appendChild(VizSVG.arrow());
      table_el.appendChild(table_arrow);
      table_arrow.onmousedown = function (e) {
        if (!viz.selected_table) {
          return;
        }
        e.stopPropagation();
        Editor.set_table_closed(viz, !viz.selected_table.closed);
        App.render();
      }

      table_el.onclick = function (e) {
        e.stopPropagation();
      }

      table_el.onmousedown = function (e) {
        e.stopPropagation();
        if (viz.selected_table) {
          Editor.set_selected_element(viz.selected_table.id);
          App.render();
        } else {
          TablePicker.render_overlay(page, viz, viz.selected_table);
        }
      }

      if (viz.selected_table && page.selected_element === viz.selected_table.id) {
        table_el.classList.add("selected");
      }

      if (viz.selected_table) {
        const menu = Menu.menu();
        table_el.appendChild(menu.container);
        Menu.add_menu_item(menu, "Replace Table", function () {
          TablePicker.render_overlay(page, viz, viz.selected_table);
        })
        table_el.id = "table_" + viz.selected_table.id;
      }

      if (viz.selected_table && viz.selected_table.closed) {
        table_el.classList.add("closed");
        return;
      }

      if (viz.selected_table === null) {
        return;
      }

      let selected_id = "";
      // try the selected_element directly, which will work for count
      if (App.selected_page && App.selected_page.selected_element) {
        selected_id = App.selected_page.selected_element;
      }
      const selected_col = Editor.get_selected_column(page);
      if (selected_col) {
        selected_id = viz.selected_table?.id + ":" + selected_col.id;
      }
      let subs_closed = false;
      for (const sel of viz.selected_columns) {
        if (sel.data.pivoted) {
          subs_closed = sel.data.closed;
        }
        if (subs_closed && sel.data.pivot) {
          continue;
        }
        const col_el = div("view-element");
        col_el.id = "con_" + sel.id;
        if (sel.data.pivot) {
          col_el.innerText = sel.data.pivot;
          col_el.classList.add("sub-element");
        } else {
          col_el.innerText = sel.data.column_name;
        }
        const vis = div("visibility");
        vis.appendChild(VizSVG.visibility_icon());
        col_el.appendChild(vis);
        if (sel.data.hidden) {
          col_el.classList.add("hidden");
        }
        vis.onmousedown = function (e) {
          e.stopPropagation();
          Editor.set_visibility(viz, sel, !sel.data.hidden);
        }
        EditorPage.add_icon(col_el, sel.data.column_type);

        const id = viz.selected_table?.id + ":" + sel.id;
        if (id === selected_id) {
          col_el.classList.add("selected");
        }

        col_el.onclick = function (e) {
          e.stopPropagation();
        }

        col_el.onmousedown = function (e) {
          Editor.set_selected_element(id);
          e.stopPropagation();
          App.render();
        }

        container.appendChild(col_el);

        if (sel.data.pivoted) {
          let all_subs_hidden = true;
          const key = Editor.data_key(sel.data, false);
          for (const s of viz.selected_columns) {
            if (s.data.pivoted) {
              continue;
            }
            const k = Editor.column_key(s.data.dataset_id, s.data.table_id, s.data.column_name, null);
            if (k === key && s.data.hidden == false) {
              all_subs_hidden = false;
              break;
            }
          }
          if (all_subs_hidden) {
            col_el.classList.add("hidden");
          }
          const vis = div("visibility");
          vis.appendChild(VizSVG.visibility_icon());
          col_el.appendChild(vis);
          vis.onmousedown = function (e) {
            e.stopPropagation();
            const new_val = !all_subs_hidden;
            Editor.set_pivot_sub_values_visibility(viz, sel, new_val);
          }

          const arrow = div("arrow-wrapper");
          arrow.appendChild(VizSVG.arrow());
          col_el.appendChild(arrow);
          arrow.onmousedown = function (e) {
            e.stopPropagation();
            Editor.set_pivot_closed(viz, sel, !sel.data.closed);
            App.render();
          }
          if (sel.data.closed) {
            col_el.classList.add("closed");
          }
        }
      }

      const count_id = viz.selected_table?.id + ":count";
      const count_el = div("view-element");
      count_el.id = count_id;
      count_el.innerText = "Count";
      container.appendChild(count_el);
      const vis = div("visibility");
      vis.appendChild(VizSVG.visibility_icon());
      count_el.appendChild(vis);
      vis.onmousedown = function (e) {
        e.stopPropagation();
        Editor.set_count_visibility(viz, !viz.count.hidden);
      }
      if (viz.count.hidden) {
        count_el.classList.add("hidden");
      }

      EditorPage.add_icon(count_el, "INTEGER");
      if (count_id === selected_id) {
        count_el.classList.add("selected");
      }

      count_el.onclick = function (e) {
        e.stopPropagation();
      }

      count_el.onmousedown = function (e) {
        Editor.set_selected_element(count_el.id);
        e.stopPropagation();
        App.render();
      }

      // TODO: remove?
      const show_add_col = false;
      if (show_add_col) {
        const add = div("view-element", "add-element");
        const add_input = App.input();
        add_input.placeholder = "Add Column";

        const show_picker = function () {
          /*if (!VizApp.selected_viz) {
            return;
          }*/
          add.classList.add("active");
          picker.style.display = "block";
          /*EditorPage.render_picker(picker, VizApp.selected_viz.selected_columns, ColumnFilter.NoFilter, "", function (col: ApiBQCol) {
            Editor.add_column(viz, col);
          });*/
        }

        const hide_picker = function () {
          if (picker.getAttribute("locked") === "true") {
            return;
          }
          add.classList.remove("active");
          picker.style.display = "none";
        }

        add_input.addEventListener('focus', function () {
          show_picker();
        });

        add_input.addEventListener('blur', function () {
          hide_picker();
        });

        add_input.oninput = function () {
          /*if (!VizApp.selected_viz) {
            return;
          }
          EditorPage.render_picker(picker, VizApp.selected_viz.selected_columns, ColumnFilter.NoFilter, add_input.value, function (col: ApiBQCol) {
            Editor.add_column(viz, col);
          });*/
        }
        const plus = VizSVG.plus_icon();
        plus.classList.add("add-icon");
        add.appendChild(plus);
        container.appendChild(add);

        add.appendChild(add_input);

        const picker = div("element-picker");
        add.onclick = function (e) {
          e.stopPropagation();
          add_input.focus();
        }

        add.appendChild(picker);
        picker.style.display = "none";
      }
    }

    for (const viz of viz_list) {
      render(viz);
    }
  }

  static render_left_rail(container: HTMLElement) {
    const d = div();
    container.appendChild(d);

    const col_container = div();
    d.appendChild(col_container);

    const viz_list: Display[] = [];
    if (App.selected_page) {
      for (const conn of App.selected_page.viz_list) {
        viz_list.push(conn.viz);
      }
    }

    container.onclick = function () {
      Editor.set_selected_element(null);
      App.render();
    }

    if (!App.selected_page) {
      return;
    }

    EditorPage.render_viz_list(App.selected_page, viz_list, col_container);

    /* TODO: remove?
    const info_row = function (label: string, val: string): HTMLElement {
      const row = div("panel-row");
      const date_label = div("label");
      date_label.innerText = label;
      const date_val = div();
      date_val.innerText = val;
      row.appendChild(date_label);
      row.appendChild(date_val);
      return row;
    }

    const table_info = div("table-info");
    const table_title = div("section-title");
    table_title.innerText = "Table Info";
    table_info.appendChild(table_title);
    const date = new Date(viz.selected_table.table.creation_time);
    table_info.appendChild(info_row("Created", date.toLocaleDateString()));
    table_info.appendChild(info_row("Rows", viz.selected_table.table.row_count.toString()));
    d.appendChild(table_info);*/

  }

  static select_row(label: string, selection: string, opts: SelectVal[]): SelectRow {
    const row = div("panel-row");
    const label_el = div("label");
    label_el.innerText = label;
    const val = div("value");
    const sel = document.createElement("select");
    sel.style.backgroundImage = "url(" + SVG.to_url(SVG.chevron()) + ")";
    for (const opt of opts) {
      const o = document.createElement("option");
      if (opt.value === selection) {
        o.selected = true;
      }
      o.value = opt.value;
      o.innerText = opt.label;
      sel.appendChild(o);
    }
    val.appendChild(sel);
    row.appendChild(label_el);
    row.appendChild(val);
    return {
      container: row,
      label_container: label_el,
      val_container: val,
      select: sel,
    }
  }

  static date_col_opts(viz: Display): SelectVal[] {
    let date_opts: SelectVal[] = [];
    for (const c of viz.selected_columns) {
      if (BQ.date_col(c.data.column_type)) {
        const v = {
          label: c.data.column_name,
          value: c.data.column_name,
        }
        date_opts.push(v);
      }
    }
    return date_opts;
  }

  static string_col_opts(viz: Display): SelectVal[] {
    let opts: SelectVal[] = [];
    for (const c of viz.selected_columns) {
      if (c.data.column_type === "STRING") {
        const v = {
          label: c.data.column_name,
          value: c.data.column_name,
        }
        opts.push(v);
      }
    }
    return opts;
  }

  static render_date_filter_settings(viz: Display, f: FilterConnection, container: HTMLElement) {

    const types = [Editor.date_recent, Editor.date_current, Editor.date_custom];
    const type_row = EditorPage.select_row("Range", f.date_mode, types);
    container.appendChild(type_row.container);

    type_row.select.onchange = function () {
      f.date_mode = type_row.select.value;
      Editor.update_filter(viz, f);
    }

    if (f.date_mode === Editor.date_recent.value || f.date_mode === Editor.date_current.value) {
      const selected = f.date_mode === Editor.date_recent.value ? f.date_recent_interval : f.date_current_interval;
      const vals = f.date_mode === Editor.date_recent.value ? Editor.date_groups_plural : Editor.date_groups;
      const int_row = EditorPage.select_row("Interval", selected, vals);
      container.appendChild(int_row.container);

      const i_inp = App.input();
      i_inp.value = f.date_recent_count.toString();

      if (f.date_mode === Editor.date_recent.value) {
        int_row.val_container.classList.add("value-flex");
        int_row.val_container.insertBefore(i_inp, int_row.val_container.firstChild);
      }

      const update_count = function () {
        const n = Number(i_inp.value);
        if (isNaN(n)) {
          return;
        }
        f.date_recent_count = n;
        Editor.update_filter(viz, f);
      }

      i_inp.onchange = function () {
        update_count();
      }

      i_inp.onkeydown = function (e) {
        if (e.code === "Enter") {
          update_count();
        }
      };

      int_row.select.onchange = function () {
        if (f.date_mode === Editor.date_recent.value) {
          f.date_recent_interval = int_row.select.value;
        } else {
          f.date_current_interval = int_row.select.value;
        }
        Editor.update_filter(viz, f);
      }
    } else {

      const date_row = function (label: string, date_str: string | null, default_date: Date, cb: Function) {
        const r = div("panel-row");
        const label_el = div("label");
        label_el.innerText = label;
        const val = div("value");
        r.appendChild(label_el);
        r.appendChild(val);

        const date_picker = div("date-picker");
        date_picker.style.backgroundImage = "url(" + SVG.to_url(SVG.chevron()) + ")";

        const auto_enabled = false;
        const min = new Date(Date.UTC(1970, 1));
        const max = new Date(Date.UTC(2037, 1));
        const opts: DateInputOptions = {
          UTC: false,
          Force24Hour: false,
          ShowCalendar: true,
          Type: DatePickerType.Date,
          ShowClear: true,
        };

        const date = date_str ? new Date(date_str) : default_date;
        const picker = viewDatePicker(date, min, max, auto_enabled, function (d: Date | null, t: InputCallbackType) {
          if (!d) {
            picker.style.display = "none";
          }
          cb(d, t);
        }, opts);
        date_picker.appendChild(picker);
        val.appendChild(date_picker);

        if (!date_str) {
          picker.style.display = "none";
        }

        let date_chevron_cancel = false;
        date_picker.onclick = function () {
          if (date_chevron_cancel) {
            return;
          }
          if (picker.style.display === "none") {
            cb(date, InputCallbackType.Enter);
          }
          picker.style.display = "block";

          const first_el = r.querySelector("div.date-selector div") as HTMLElement;
          if (first_el) {
            first_el.focus();
          }
        }

        date_picker.onmousedown = function () {
          const selector = r.querySelector("div.date-selector-wrapper");
          if (selector && selector.classList.contains("focused")) {
            date_chevron_cancel = true;
          } else {
            date_chevron_cancel = false;
          }
        }
        return r;
      }

      const date_str = function (d: Date): string {
        return d.getFullYear() + "-" + (d.getMonth() + 1) + "-" + d.getDate();
      }

      let s_default = new Date();
      s_default.setMonth(s_default.getMonth() - 1);
      const s = date_row("Start", f.date_min, s_default, function (d: Date | null, _t: InputCallbackType) {
        console.log(d);
        let val: string | null = null;
        if (d) {
          val = date_str(d);
        }
        f.date_min = val;
        Editor.update_filter(viz, f);
      });
      container.appendChild(s);

      const e_default = new Date();
      const e = date_row("End", f.date_max, e_default, function (d: Date | null, _t: InputCallbackType) {
        console.log(d);
        let val: string | null = null;
        if (d) {
          val = date_str(d);
        }
        f.date_max = val;
        Editor.update_filter(viz, f);
      });
      container.appendChild(e);
    }
  }

  static render_text_filter_settings(viz: Display, f: FilterConnection, container: HTMLElement) {

    const vals = [Editor.text_match, Editor.text_not_match];
    const match_row = EditorPage.select_row("Match", f.text_mode, vals);
    container.appendChild(match_row.container);

    const val_row = EditorPage.select_row("Value", "", []);
    val_row.val_container.innerHTML = "";
    const inp = App.input();
    if (f.text_query) {
      inp.value = f.text_query;
    }
    val_row.val_container.appendChild(inp);
    container.appendChild(val_row.container);

    inp.onchange = function () {
      f.text_query = inp.value;
      Editor.update_filter(viz, f);
    }
  }

  static render_filter(viz: Display, container: HTMLElement) {
    const section = div("section");
    const title = div("section-title", "first-section");
    title.innerText = "Filter";
    section.appendChild(title);

    let col_opts: SelectVal[] = [];
    for (const c of viz.selected_columns) {
      if (BQ.date_col(c.data.column_type) || c.data.column_type === "STRING") {
        const v = {
          label: c.data.column_name,
          value: c.data.column_name,
        }
        col_opts.push(v);
      }
    }

    for (const f of viz.filters) {

      const f_el = div("filter");
      const col_row = EditorPage.select_row("Column", f.column, col_opts);
      f_el.appendChild(col_row.container);
      const minus = VizSVG.minus();
      minus.onclick = function () {
        Editor.remove_filter(viz, f);
      }
      col_row.label_container.appendChild(minus);
      col_row.select.onchange = function () {
        f.column = col_row.select.value;
        Editor.update_filter(viz, f);
      }

      // get matching the col
      let col: BQData | null = null;
      for (const c of viz.selected_columns) {
        if (c.data.column_name === f.column) {
          col = c.data;
          break;
        }
      }

      if (col) {
        if (BQ.date_col(col.column_type)) {
          EditorPage.render_date_filter_settings(viz, f, f_el);
        } else {
          EditorPage.render_text_filter_settings(viz, f, f_el);
        }
      }

      section.appendChild(f_el);
    }

    const plus = VizSVG.plus();
    plus.onclick = function () {
      if (col_opts.length > 0) {
        Editor.add_filter(viz, col_opts[0].value);
      }
    }
    title.appendChild(plus);
    container.appendChild(section);
  }

  static render_group(viz: Display, container: HTMLElement) {
    const section = div("section");
    const group_title = div("section-title", "first-section");
    group_title.innerText = "Group By";
    section.appendChild(group_title);

    let date_opts = EditorPage.date_col_opts(viz);

    if (viz.group_by) {

      const selected = viz.group_by ? viz.group_by : "";
      const group_row = EditorPage.select_row("Column", selected, date_opts);
      section.appendChild(group_row.container);

      group_row.select.onchange = function () {
        Editor.set_group_by(viz, group_row.select.value, int_row.select.value);
      }

      const selected_int = viz.group_by_interval ? viz.group_by_interval : "";
      const int_row = EditorPage.select_row("Interval", selected_int, Editor.date_groups);
      section.appendChild(int_row.container);

      int_row.select.onchange = function () {
        Editor.set_group_by(viz, group_row.select.value, int_row.select.value);
      }
    }

    if (viz.group_by) {
      const minus = VizSVG.minus();
      minus.onclick = function () {
        Editor.set_group_by(viz, null, null);
      }
      group_title.appendChild(minus);

    } else {
      const plus = VizSVG.plus();
      plus.onclick = function () {
        if (date_opts.length > 0) {
          Editor.set_group_by(viz, date_opts[0].value, Editor.date_groups[0].value);
        }
      }
      group_title.appendChild(plus);
    }
    container.appendChild(section);
  }

  static render_sort(viz: Display, container: HTMLElement) {
    const section = div("section");
    const title = div("section-title", "first-section");
    title.innerText = "Sort";
    section.appendChild(title);

    let date_opts = EditorPage.date_col_opts(viz);

    if (viz.sort) {
      const selected = viz.sort ? viz.sort : "";
      const sort_row = EditorPage.select_row("Column", selected, date_opts);
      section.appendChild(sort_row.container);

      sort_row.select.onchange = function () {
        Editor.set_sort(viz, sort_row.select.value, order_row.select.value);
      }

      const sort_selected = viz.sort_order ? viz.sort_order : "";
      const order_row = EditorPage.select_row("Order", sort_selected, Editor.sort_orders);
      section.appendChild(order_row.container);

      order_row.select.onchange = function () {
        Editor.set_sort(viz, sort_row.select.value, order_row.select.value);
      }
    }

    if (viz.sort) {
      const minus = VizSVG.minus();
      minus.onclick = function () {
        Editor.set_sort(viz, null, null);
      }
      title.appendChild(minus);

    } else {
      const plus = VizSVG.plus();
      plus.onclick = function () {
        if (viz.selected_columns.length === 0) {
          return;
        }
        const column_name = date_opts.length > 0 ? date_opts[0].value : viz.selected_columns[0].data.column_name;
        Editor.set_sort(viz, column_name, Editor.sort_orders[0].value);
      }
      title.appendChild(plus);
    }
    container.appendChild(section);
  }

  static render_pivot_opts(display: Display, col: DataConnection, container: HTMLElement) {
    const is_date = BQ.date_col(col.data.column_type);
    if (is_date) {
      const facet_row = div("panel-row");
      const facet_label = div("label");
      facet_label.innerText = "Facet";
      const facet_val = div("value");
      const facet_sel = document.createElement("select");
      facet_sel.style.backgroundImage = "url(" + SVG.to_url(SVG.chevron()) + ")";
      facet_val.appendChild(facet_sel);
      facet_row.appendChild(facet_label);
      facet_row.appendChild(facet_val);
      container.appendChild(facet_row);

      for (const opt of Editor.date_pivots) {
        const o = document.createElement("option");
        if (col.data.pivot_facet === opt) {
          o.selected = true;
        }
        o.value = opt;
        o.innerText = opt;
        facet_sel.appendChild(o);
      }
    }

    const row = div("panel-row");
    const label = div("label");
    label.innerText = "Measure";
    const val = div("value");
    const sel = document.createElement("select");
    sel.style.backgroundImage = "url(" + SVG.to_url(SVG.chevron()) + ")";

    const count = document.createElement("option");
    if (!col.data.pivot_op) {
      count.selected = true;
    }
    count.value = "Count";
    count.innerText = "Count";
    sel.appendChild(count);

    const sum = document.createElement("option");
    if (col.data.pivot_op) {
      sum.selected = true;
    }
    sum.value = "Sum";
    sum.innerText = "Sum";
    sel.appendChild(sum);

    val.appendChild(sel);

    sel.onchange = function () {
      if (sel.value === "Count") {
        Editor.set_pivot_op(display, col, null);
      } else {
        Editor.set_pivot_op(display, col, "SUM");
        Editor.add_pivot_measure(display, col);
      }
    }

    row.appendChild(label);
    row.appendChild(val);
    container.appendChild(row);

    if (col.data.pivot_op) {
      for (const measure of col.data.pivot_measures) {
        const r = div("panel-row");
        const l = div("label");
        l.innerText = "Column";
        const v = div("value");
        const s = document.createElement("select");

        s.style.backgroundImage = "url(" + SVG.to_url(SVG.chevron()) + ")";

        if (display.selected_table && display.selected_table.table.columns) {
          for (const c of display.selected_table.table.columns) {
            if (!BQ.numeric_col(c.type)) {
              continue;
            }
            const op = document.createElement("option");
            if (c.name === measure.column) {
              op.selected = true;
            }
            for (const m of col.data.pivot_measures) {
              if (m.column === c.name && m !== measure) {
                op.disabled = true
              }
            }
            op.value = c.name;
            op.innerText = c.name;
            s.appendChild(op);
          }
        }

        s.onchange = function () {
          Editor.update_pivot_measure(display, col, measure, s.value);
        }

        v.appendChild(s);
        r.appendChild(l);
        r.appendChild(v);
        container.appendChild(r);
      }
    }
  }

  static render_pivot(page: Page, display: Display, container: HTMLElement) {
    const selected_col = Editor.get_selected_column(page);
    if (!selected_col || !display.selected_table) {
      return;
    }
    const pivot = div("section");
    const pivot_title = div("section-title");
    pivot_title.innerText = "Pivot";
    pivot.appendChild(pivot_title);
    container.appendChild(pivot);

    // pivot is for date or string
    const is_string = selected_col.data.column_type === "STRING";
    const is_date = BQ.date_col(selected_col.data.column_type);
    if (!is_date && !is_string) {
      pivot.classList.add("disabled");
    }

    if (selected_col.data.pivoted) {
      const minus = VizSVG.minus();
      minus.onclick = function () {
        Editor.remove_pivot(selected_col);
      }
      pivot_title.appendChild(minus);
      //if (is_string) {
      EditorPage.render_pivot_opts(display, selected_col, pivot);
      //}
    } else {
      const plus = VizSVG.plus();
      if ((is_string || is_date) && !selected_col.data.pivot) {
        plus.onclick = function () {
          Editor.add_pivot(display, selected_col);
        }
      }
      pivot_title.appendChild(plus);
    }
  }

  static render_operation(page: Page, viz: Display, container: HTMLElement) {
    const selected_col = Editor.get_selected_column(page);
    if (!selected_col) {
      return;
    }

    const section = div("section");
    const op_title = div("section-title");
    op_title.innerText = "Operation";
    section.appendChild(op_title);

    // op is currently number only
    if (!BQ.numeric_col(selected_col.data.column_type)) {
      section.classList.add("disabled");
    }

    if (selected_col.data.operation) {
      const minus = VizSVG.minus();
      minus.onclick = function () {
        Editor.set_operation(viz, selected_col, null);
      }
      op_title.appendChild(minus);

      const op_row = EditorPage.select_row("Measure", selected_col.data.operation, Editor.operations);
      section.appendChild(op_row.container);

      op_row.select.onchange = function () {
        Editor.set_operation(viz, selected_col, op_row.select.value);
      }
    } else {
      const plus = VizSVG.plus();
      plus.onclick = function () {
        if (!selected_col || !BQ.numeric_col(selected_col.data.column_type)) {
          return;
        }
        Editor.set_operation(viz, selected_col, Editor.operations[0].value);
      }
      op_title.appendChild(plus);
    }
    container.appendChild(section);
  }

  static render_count(viz: Display, container: HTMLElement) {
    const section = div("section");
    const op_title = div("section-title");
    op_title.innerText = "Count";
    section.appendChild(op_title);
    container.appendChild(section);

    let opts = EditorPage.string_col_opts(viz);

    const none: SelectVal = {
      label: "None",
      value: "",
    }
    opts.unshift(none);

    const selected = viz.count_target ? viz.count_target : "";
    const row = EditorPage.select_row("Column", selected, opts);
    section.appendChild(row.container);

    row.select.onchange = function () {
      Editor.set_count(viz, row.select.value);
    }
  }

  static render_line_style_control(viz: Display, container: HTMLElement) {
    const row = div("panel-row");
    const label = div("label");
    label.innerText = "Style";
    const val = div("value");

    row.appendChild(label);
    row.appendChild(val);
    container.appendChild(row);

    const selector = div("selector", "selector-full", "large-icon-selector");
    const types = ["line", "step"];
    for (const t of types) {
      const opt = div("option");
      if (t === "line") {
        opt.appendChild(VizSVG.line_style_icon());
      } else {
        opt.appendChild(VizSVG.step_style_icon());
      }
      selector.appendChild(opt);
      if (viz.line_style === t) {
        opt.classList.add("selected");
      }
      opt.onclick = function () {
        const els = selector.querySelectorAll("div.option");
        for (let i = 0; i < els.length; i++) {
          const el = els[i];
          el.classList.remove("selected");
        }
        opt.classList.add("selected");
        viz.line_style = t;
        const attr: ApiAttribute = {
          parent: viz.id,
          key: "line_style",
          text: t,
          number: null,
        }
        const update: ApiUpdate = {
          attributes: [attr],
          connections: [],
        }
        App.new_graph_data = true;
        const opts: UpdateOpts = {
          update_thumbnail: true,
          data_changes: false,
        }
        Api.update_display(viz.id, update, opts);
        App.render();
      }
    }
    val.appendChild(selector);
  }

  static render_curve_control(viz: Display, container: HTMLElement) {

    const row = div("panel-row");
    const label = div("label");
    label.innerText = "Curve";
    const val = div("value", "value-flex", "slider");
    const amt = App.input();
    amt.value = viz.line_curve.toString();
    const slide = input();
    slide.type = "range";
    slide.value = viz.line_curve.toString();
    val.appendChild(amt);
    val.appendChild(slide);

    row.appendChild(label);
    row.appendChild(val);
    container.appendChild(row);

    const update = function (n: number, api: boolean) {
      viz.line_curve = n;
      if (api) {
        const attr: ApiAttribute = {
          parent: viz.id,
          key: "line_curve",
          text: null,
          number: n,
        }
        const update: ApiUpdate = {
          attributes: [attr],
          connections: [],
        }
        const opts: UpdateOpts = {
          update_thumbnail: true,
          data_changes: false,
        }
        Api.update_display(viz.id, update, opts);
      }
      const g_id = "viz_" + viz.id;
      const g = document.querySelector("svg #" + g_id) as SVGGElement;
      Display.render(viz, g, 18);
    }

    const apply_amt_value = function () {
      let n = Number(amt.value);
      if (isNaN(n)) {
        return;
      }
      n = Math.max(0, n);
      n = Math.min(100, n);
      slide.value = n.toString();
      update(n, true);
    }

    amt.onkeydown = function (e) {
      if (e.keyCode === 13) { // enter
        apply_amt_value();
      }
    };

    amt.addEventListener('blur', function () {
      apply_amt_value();
    })

    slide.oninput = function () {
      amt.value = slide.value;
      update(Number(slide.value), false);
    }

    slide.onchange = function () {
      amt.value = slide.value;
      update(Number(slide.value), true);
    }
  }

  static render_graph_settings(viz: Display, container: HTMLElement) {
    const title = div("section", "section-title", "first-section");
    const selector = div("selector", "selector-full", "icon-selector");
    const types = ["bars", "lines", "number"];
    for (const t of types) {
      const opt = div("option");
      opt.appendChild(VizSVG.graph_icon(t));
      selector.appendChild(opt);
      if (viz.type === t) {
        opt.classList.add("selected");
      }
      opt.onclick = function () {
        const els = selector.querySelectorAll("div.option");
        for (let i = 0; i < els.length; i++) {
          const el = els[i];
          el.classList.remove("selected");
        }
        opt.classList.add("selected");
        viz.type = t;
        const attr: ApiAttribute = {
          parent: viz.id,
          key: "type",
          text: t,
          number: null,
        }
        const update: ApiUpdate = {
          attributes: [attr],
          connections: [],
        }
        App.new_graph_data = true;
        const opts: UpdateOpts = {
          update_thumbnail: true,
          data_changes: false,
        }
        Api.update_display(viz.id, update, opts);
        App.render();
      }
    }
    title.appendChild(selector);
    container.appendChild(title);

    if (viz.type === "lines") {
      const section = div("section");
      const title = div("section-title");
      title.innerText = "Shape";
      section.appendChild(title);
      container.appendChild(section);

      EditorPage.render_line_style_control(viz, section);
      EditorPage.render_curve_control(viz, section);

    }
  }

  static render_right_rail(page: Page, viz: Display | null, container: HTMLElement) {
    container.innerHTML = "";
    if (!viz) {
      return;
    }
    EditorPage.render_graph_settings(viz, container);
    EditorPage.render_filter(viz, container);
    EditorPage.render_group(viz, container);
    EditorPage.render_sort(viz, container);

    // check if count is selected
    if (page.selected_element) {
      const parts = page.selected_element.split(":");
      if (parts.length > 1 && parts[1] === "count") {
        const col = div("section", "divider");
        const col_title = div("section-title");
        col_title.innerText = "Count";
        col.appendChild(col_title);
        container.appendChild(col);
        EditorPage.render_count(viz, container);
        return;
      }
    }

    const selected_col = Editor.get_selected_column(page);
    if (!selected_col) {
      return;
    }

    const col = div("section", "divider");
    const col_title = div("section-title");
    col_title.innerText = selected_col.data.column_name;
    col.appendChild(col_title);
    container.appendChild(col);

    EditorPage.render_pivot(page, viz, container);
    EditorPage.render_operation(page, viz, container);
  }

  private static render_panels(page: Page, container: HTMLElement) {
    const graph_width = 400;
    const inset = 20;
    const width = (graph_width * 2) + (inset * 2);
    const panel = div("graph-page", "display_00c0f3");
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("viewBox", "0 0 " + width + " 400");
    const defs = document.createElementNS("http://www.w3.org/2000/svg", "defs");
    const filter = document.createElementNS("http://www.w3.org/2000/svg", "filter");
    filter.id = "shadow";
    filter.setAttribute("x", "-20%");
    filter.setAttribute("y", "-20%");
    filter.setAttribute("width", "140%");
    filter.setAttribute("height", "140%");
    filter.setAttribute("filterUnits", "objectBoundingBox");
    filter.setAttribute("color-interpolation-filters", "linearRGB");

    const shadow = document.createElementNS("http://www.w3.org/2000/svg", "feDropShadow");
    shadow.setAttribute("dx", "0");
    shadow.setAttribute("dy", "0");
    shadow.setAttribute("stdDeviation", "4");
    shadow.setAttribute("flood-opacity", "0.2");
    filter.appendChild(shadow);
    defs.appendChild(filter);
    svg.appendChild(defs);

    panel.appendChild(svg);
    container.appendChild(panel);

    let left_height = inset;
    let right_height = inset;

    let drag_x = 0;
    let drag_y = 0;
    let moved = false;
    let target_id = "";

    let node_set: DisplayNode[] = [];
    const spacers: DisplaySpacer[] = [];

    const spacer_height = 200;
    const render_spacer = function (group: string, position: number, left_offset: number, top_offset: number): DisplaySpacer {
      const r = SVG.rect(left_offset, top_offset, graph_width, spacer_height);
      r.setAttribute("fill", "none");
      svg.appendChild(r);
      const s: DisplaySpacer = {
        group: group,
        position: position,
        element: r,
      }
      spacers.push(s);
      return s;
    }
    const l_spacer = render_spacer("left", 0, inset, left_height);
    const r_spacer = render_spacer("right", 0, (inset + graph_width), right_height);

    const y_offset = function (node: DisplayNode) {
      let offset = inset;
      for (const n of node_set) {
        if (n.group !== node.group) {
          continue;
        }
        if (n.position < node.position) {
          offset += n.height;
        }
      }
      return offset;
    }

    const reflow = function () {
      for (const n of node_set) {
        if (n.element.id === target_id) {
          continue;
        }
        let x = n.x;
        const y = y_offset(n);
        const transform = "translate(" + x + ", " + y + ")";
        n.element.setAttribute("transform", transform);
      }
    }

    const move_node = function (node: DisplayNode, group: string, pos: number) {
      node.position = pos;
      node.group = group;

      let l_idx = 0;
      let r_idx = 0;
      const sorted = node_set.sort(function (a, b) {
        return a.position - b.position;
      });
      for (const n of sorted) {
        if (n.group === "left") {
          n.position = l_idx;
          l_idx++;
        } else {
          n.position = r_idx;
          r_idx++;
        }
      }
      node_set = sorted;
    }

    const render = function (v: VizConnection, group: string, position: number, left_offset: number, top_offset: number) {
      const g = document.createElementNS("http://www.w3.org/2000/svg", "g");
      g.classList.add("display");
      g.id = "viz_" + v.viz.id;
      const transform = "translate(" + left_offset + ", " + top_offset + ")";
      g.setAttribute("transform", transform);
      svg.appendChild(g);

      g.onclick = function (e) {
        e.stopPropagation();
        Editor.set_selected_element(v.viz.id);
        App.render();
      }
      const selected_viz = Editor.get_selected_viz(page);
      if (selected_viz && selected_viz.id === v.viz.id) {
        g.classList.add("selected");
      } else {
        g.classList.remove("selected");
      }

      const f = App.input();
      const f_x_offset = 18;
      const f_y_offset = 22;
      f.style.left = (left_offset + f_x_offset) + "px";
      f.style.top = (top_offset + f_y_offset) + "px";
      f.value = v.viz.title;
      panel.appendChild(f);

      f.onmousedown = function (e) {
        e.stopPropagation();
      }

      f.onclick = function (e) {
        e.stopPropagation();
      }

      f.addEventListener('blur', function () {
        App.input_focused = false;
        if (!f.getAttribute("cancel")) {
          const req: ApiDisplayUpdate = {
            title: f.value
          }
          Ajax.post("/api/v1/displays/" + v.viz.id, function () {
            v.viz.title = f.value;
            const v_id = "viz_" + v.viz.id;
            const nav_el = document.querySelector("div.view-element#" + v_id + " span");
            if (nav_el) {
              nav_el.innerHTML = f.value;
            }
            const title_el = document.querySelector("#t_" + v.viz.id);
            if (title_el) {
              title_el.innerHTML = f.value.toUpperCase();
            }
          }, req, function () {
            f.value = v.viz.title;
          });
        }
        window.setTimeout(function () {
          f.removeAttribute("cancel");
        }, 50);
      })

      f.onkeydown = function (e) {
        if (e.code === "Escape") {
          f.setAttribute("cancel", "true");
          f.blur();
          f.value = v.viz.title;
        } else if (e.code === "Enter" || e.code === "Return") {
          f.blur();
        }
      }

      const size = Display.render(v.viz, g, 18);
      if (left_offset === inset) {
        left_height += size.height;
      } else {
        right_height += size.height;
      }

      const node: DisplayNode = {
        viz: v,
        group: group,
        position: position,
        element: g,
        x: left_offset,
        y: top_offset,
        height: size.height,
      }

      const drag_move = function (e: MouseEvent) {
        const x_delta = (drag_x - e.clientX) / App.zoom;
        const y_delta = (drag_y - e.clientY) / App.zoom;

        if ((Math.abs(x_delta) > 5 || Math.abs(y_delta) > 5) && !moved) {
          target_id = g.id;
          const list = svg.querySelectorAll("g.display");
          if (list.length > 0) {
            list[list.length - 1].after(g);
          }
          moved = true;
        }

        const transform = "translate(" + (left_offset - x_delta) + ", " + (top_offset - y_delta) + ")";
        g.setAttribute("transform", transform);
        panel.classList.add("move");

        for (const n of node_set) {
          if (n.element.id === g.id) {
            continue;
          }
          const r = n.element.getBoundingClientRect();
          if (r.left < e.clientX && r.right > e.clientX && r.top < e.clientY && r.bottom > e.clientY) {
            if (e.clientY > (r.top + r.height / 2)) {
              move_node(node, n.group, n.position + 0.5);
            } else {
              move_node(node, n.group, n.position - 0.5);
            }
          }
        }
        for (const s of spacers) {
          const r = s.element.getBoundingClientRect();
          if (r.left < e.clientX && r.right > e.clientX && r.top < e.clientY && r.bottom > e.clientY) {
            move_node(node, s.group, s.position);
          }
        }
        reflow();

        e.preventDefault();
      }

      const on_drag_move = function (e: MouseEvent) {
        window.requestAnimationFrame(function () {
          drag_move(e);
        });
      }

      const drag_end = function () {
        panel.classList.remove("move");
        document.removeEventListener('mousemove', on_drag_move);
        document.removeEventListener('mouseup', drag_end);
        if (!moved) {
          return;
        }

        const update: ApiUpdate = {
          attributes: [],
          connections: [],
        }
        for (const n of node_set) {
          n.viz.position = n.position;
          const pos: ApiAttribute = {
            parent: n.viz.id,
            key: "position",
            text: null,
            number: n.position,
          }
          const group: ApiAttribute = {
            parent: n.viz.id,
            key: "group",
            text: n.group,
            number: null,
          }
          update.attributes.push(pos);
          update.attributes.push(group);
          if (App.selected_page) {
            for (const v of page.viz_list) {
              if (v.id === n.viz.id) {
                v.group = n.group;
                v.position = n.position;
              }
            }
            page.viz_list.sort(function (a, b) {
              return a.position - b.position;
            });
          }
        }
        App.render();
        Api.update_page(page, update, true);
      }

      g.onmousedown = function (e) {
        drag_x = e.clientX;
        drag_y = e.clientY;
        moved = false;

        document.addEventListener('mousemove', on_drag_move);
        document.addEventListener('mouseup', drag_end);
        e.preventDefault();
      }

      node_set.push(node);
    }
    let left_idx = 0;
    let right_idx = 0;
    for (const v of page.viz_list) {
      const x = v.group === "left" ? inset : inset + graph_width;
      const y = v.group === "left" ? left_height : right_height;
      render(v, v.group, v.position, x, y);
      if (v.group === "left") {
        left_idx++;
      } else {
        right_idx++;
      }
    }
    l_spacer.position = left_idx;
    r_spacer.position = right_idx;
    l_spacer.element.setAttribute("y", left_height + "px");
    r_spacer.element.setAttribute("y", right_height + "px");

    const height = Math.max(left_height, right_height) + inset;
    svg.setAttribute("viewBox", "0 0 " + width + " " + (height + spacer_height) + "");

    if (App.new_graph_data && App.thumbnail_dirty_count > 0) {
      App.thumbnail_dirty_count--;
    }
    if (App.new_graph_data && App.thumbnail_dirty_count === 0) {
      App.thumbnail_dirty_count--;
      SVG.render_to_image(svg, page.id, Api.upload_thumbnail);

      const rect = svg.getBoundingClientRect();
      const ratio = rect.width / rect.height;
      const attr: ApiAttribute = {
        parent: page.id,
        key: "aspect_ratio",
        text: null,
        number: ratio,
      }
      const update: ApiUpdate = {
        attributes: [attr],
        connections: [],
      }
      Api.update_page(page, update);
    }

  }

  static set_transform_origin() {
    const page = document.querySelector("div.graph-page") as HTMLElement;
    const center = document.querySelector("div.center");
    if (page && center) {
      const w = center.getBoundingClientRect().width;
      const h = center.getBoundingClientRect().height;
      const cx = (w / 2) - App.translate_x;
      const cy = (h / 2) - App.translate_y;
      page.style.transformOrigin = cx + "px " + cy + "px";
    }
  }

  static render_main(container: HTMLElement) {
    if (!App.selected_page) {
      return;
    }

    let center = container.querySelector("div.center") as HTMLElement;
    if (!center) {
      center = div("center");
      container.appendChild(center);
      center.onclick = function () {
        Editor.set_selected_element(null);
        App.render();
      }

      center.onwheel = function (e) {
        e.preventDefault();
        App.translate_x -= (e.deltaX * 0.8) / App.zoom;
        App.translate_y -= (e.deltaY * 0.8) / App.zoom;
        document.body.style.setProperty("--translate-x", App.translate_x + "");
        document.body.style.setProperty("--translate-y", App.translate_y + "");
        EditorPage.set_transform_origin();
      }
    }
    center.innerHTML = "";

    EditorPage.render_panels(App.selected_page, center);
    EditorPage.set_transform_origin();

    let right = container.querySelector("div.right") as HTMLElement;
    if (!right) {
      right = div("right");
      container.appendChild(right);
    }

    const selected_viz = Editor.get_selected_viz(App.selected_page);
    EditorPage.render_right_rail(App.selected_page, selected_viz, right);

    let bottom = container.querySelector("div.bottom") as HTMLElement;
    if (!bottom) {
      bottom = div("bottom", "data");
      container.appendChild(bottom);
    }

    EditorPage.render_data(App.selected_page, selected_viz, bottom);
    if (App.selected_page.data_panel_open) {
      container.classList.add("data-open");
    } else {
      container.classList.remove("data-open");
    }
  }

  static update_zoom_ui() {
    const zoom = document.querySelector("div.zoom-control") as HTMLElement;
    if (!zoom) {
      return;
    }
    const label = zoom.querySelector("span.zoom-label") as HTMLElement;
    const plus = zoom.querySelector("svg.add-icon");
    const minus = zoom.querySelector("svg.minus-icon");
    if (!label || !plus || !minus) {
      return;
    }

    label.innerText = Math.round(App.zoom * 100) + "%";
    if (App.zoom <= 0.25) {
      minus.classList.add("disabled");
    } else {
      minus.classList.remove("disabled");
    }
    if (App.zoom >= 4) {
      plus.classList.add("disabled");
    } else {
      plus.classList.remove("disabled");
    }
  }

  static render_nav(nav: HTMLElement) {
    nav.innerHTML = "";

    if (App.selected_page) {
      const page = App.selected_page;
      const h1 = document.createElement("h1");
      nav.appendChild(h1);
      const f = App.input();
      f.value = page.title;
      h1.appendChild(f);

      f.addEventListener('blur', function () {
        App.input_focused = false;
        if (!f.getAttribute("cancel")) {
          const req: ApiPageUpdate = {
            title: f.value
          }
          Ajax.post("/api/v1/pages/" + page.id, function () {
            page.title = f.value;
          }, req, function () {
            f.value = page.title;
          });
        }
        window.setTimeout(function () {
          f.removeAttribute("cancel");
        }, 50);
      })

      f.onkeydown = function (e) {
        if (e.code === "Escape") {
          f.setAttribute("cancel", "true");
          f.blur();
          f.value = page.title;
        } else if (e.code === "Enter" || e.code === "Return") {
          f.blur();
        }
      }
    }

    const spacer = div("spacer");
    nav.appendChild(spacer);

    const buttons = div();
    const add_display = VizSVG.display_icon_large();
    buttons.appendChild(add_display);
    nav.appendChild(buttons);

    add_display.onclick = function () {
      Editor.add_new_viz();
    }

    const divider = div("divider");
    nav.appendChild(divider);

    const zoom = div("zoom-control");
    const minus = SVG.minus_icon(26, 10);
    const label_wrap = span("zoom-label-wrap");
    const label = span("zoom-label", "no-select");
    label_wrap.appendChild(label);
    label.innerText = "100%";
    const plus = SVG.plus_icon(26, 10);
    zoom.appendChild(minus);
    zoom.appendChild(label_wrap);
    zoom.appendChild(plus);
    nav.appendChild(zoom);

    minus.onclick = function () {
      Editor.zoom_out();
    }

    plus.onclick = function () {
      Editor.zoom_in();
    }

    const menu = Menu.menu();
    Menu.add_menu_item(menu, "Zoom In", function () {
      Editor.zoom_in();
    }, "+");
    Menu.add_menu_item(menu, "Zoom Out", function () {
      Editor.zoom_out();
    }, "-");
    Menu.add_menu_item(menu, "Zoom to Fit", function () {
      Editor.zoom_to_fit();
    }, "1");
    Menu.add_menu_item(menu, "Zoom to 100%", function () {
      Editor.zoom_reset();
    }, "0");
    label_wrap.appendChild(menu.container);

    nav.appendChild(div("spacer"));

    const button = div("button", "run-button");
    if (App.updating_count > 0) {
      button.classList.add("working");
    }
    button.innerText = "Run";
    const loading = Loading.overlay();
    button.appendChild(loading);
    nav.appendChild(button);
    button.onclick = function () {
      Editor.run();
    }

    const view_button = anchor("button", "light", "icon");
    view_button.appendChild(VizSVG.open_icon());
    nav.appendChild(view_button);
    view_button.href = "/page/" + App.selected_page?.id;
    view_button.onclick = function (e) {
      if (e.button === 0 && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        window.open(view_button.href, "_blank");
      }
    }

    EditorPage.update_zoom_ui();
  }

}